// Brief Prospection Phase 2 — Dashboard expert : visites, réservations
// attribuées, conversion par canal de partage, tendance 14j, top sessions.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, CalendarCheck, TrendingUp, Users, Share2, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SharePanel from '../components/SharePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid={testId}>
    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wide">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-white">{value}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

export default function ProspectionDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [handle, setHandle] = useState(user?.handle || '');
  // Brief Jul 27 2026 — sélecteur de période 7 / 30 / 90 jours (backend clamp 1-90).
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API}/prospection/stats?days=${days}`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    if (!handle) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then((r) => setHandle(r.data.handle || ''))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#7F77DD]" />
      </div>
    );
  }

  const origin = window.location.origin;
  const maxDay = Math.max(1, ...(stats?.daily || []).map((d) => d.visits));
  const maxChannel = Math.max(1, ...(stats?.channels || []).map((c) => c.visits));
  const hasData = (stats?.visits_total || 0) > 0 || (stats?.bookings_attributed || 0) > 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="prospection-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">Prospection</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            L'impact de vos liens partagés — {days} derniers jours
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Brief Jul 27 2026 — sélecteur de période */}
          <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5" data-testid="prospection-period-selector">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                data-testid={`prospection-period-${d}`}
                className={`px-3 py-1.5 rounded-[10px] text-xs font-semibold transition-colors ${
                  days === d
                    ? 'bg-[#7F77DD] text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {d} j
              </button>
            ))}
          </div>
          <button
            onClick={() => setShareOpen(true)}
            disabled={!handle}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ backgroundColor: '#7F77DD' }}
            data-testid="prospection-share-btn"
          >
            <Share2 className="h-4 w-4" /> Partager mon profil
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Eye} label="Visites" value={stats?.visits_total ?? 0}
          hint={`${stats?.profile_visits ?? 0} profil · ${stats?.event_visits ?? 0} sessions`}
          testId="prospection-stat-visits" />
        <StatCard icon={CalendarCheck} label="Réservations attribuées" value={stats?.bookings_attributed ?? 0}
          hint={`sur ${stats?.bookings_total ?? 0} au total`} testId="prospection-stat-bookings" />
        <StatCard icon={TrendingUp} label="Conversion" value={`${stats?.conversion_rate ?? 0}%`}
          hint="réservations / visites" testId="prospection-stat-conversion" />
        <StatCard icon={Users} label="Abonnés" value={stats?.follower_count ?? 0}
          hint="notifiés à chaque publication" testId="prospection-stat-followers" />
      </div>

      {!hasData && (
        <div className="p-8 rounded-2xl bg-[#7F77DD]/5 border border-[#7F77DD]/20 text-center" data-testid="prospection-empty">
          <p className="font-bold text-gray-900 dark:text-white">Aucune visite mesurée pour l'instant</p>
          <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto">
            Partagez votre lien public sur LinkedIn ou WhatsApp — chaque visite et
            chaque réservation qui en découle apparaîtra ici, canal par canal.
          </p>
        </div>
      )}

      {/* Par canal */}
      {(stats?.channels || []).length > 0 && (
        <section data-testid="prospection-channels">
          <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">Par canal de partage</h2>
          <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70 space-y-3">
            {stats.channels.map((c) => (
              <div key={c.ref} data-testid={`prospection-channel-${c.ref}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{c.label}</span>
                  <span className="text-gray-500">
                    {c.visits} visite{c.visits > 1 ? 's' : ''}
                    {c.bookings > 0 && <span className="text-emerald-600 font-semibold"> · {c.bookings} réservation{c.bookings > 1 ? 's' : ''}</span>}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${Math.max(4, (c.visits / maxChannel) * 100)}%`,
                    backgroundColor: '#7F77DD',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tendance 14 jours */}
      {hasData && (
        <section data-testid="prospection-daily">
          <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">Visites — 14 derniers jours</h2>
          <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
            <div className="flex items-end gap-1.5" style={{ height: 112 }}>
              {(stats?.daily || []).map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${d.date} : ${d.visits} visites`}>
                  <div className="w-full rounded-t-md transition-all" style={{
                    height: `${Math.max(3, (d.visits / maxDay) * 88)}px`,
                    backgroundColor: d.visits > 0 ? '#7F77DD' : '#e5e7eb',
                  }} />
                  <span className="text-[9px] text-gray-400">{d.date.slice(8)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top sessions */}
      {(stats?.top_events || []).length > 0 && (
        <section data-testid="prospection-top-events">
          <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">Sessions les plus visitées</h2>
          <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70 divide-y divide-gray-100 dark:divide-gray-700">
            {stats.top_events.map((e) => (
              <div key={e.event_id} className="flex items-center justify-between px-5 py-3.5" data-testid={`prospection-event-${e.event_id}`}>
                <p className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate pr-4">{e.title}</p>
                <p className="text-sm text-gray-500 flex-shrink-0">
                  {e.visits} visite{e.visits > 1 ? 's' : ''}
                  {e.bookings > 0 && <span className="text-emerald-600 font-semibold"> · {e.bookings} rés.</span>}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <SharePanel
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Partager mon profil"
        shareUrl={`${origin}/api/@${handle}`}
        prettyUrl={`${origin}/@${handle}`}
        defaultMessage={'Découvrez mon profil et réservez une session avec moi 👇'}
      />
    </div>
  );
}
